/**
 * useNfcStatus
 *
 * Subscribes to the nfcConnector singleton and returns a reactive status value.
 *
 * States:
 *   disconnected  — Shaka Bridge not reachable (gray)
 *   connecting    — attempting connection (amber, pulsing)
 *   no_reader     — Shaka Bridge connected but no NFC reader detected (orange)
 *   ready         — connected + reader available (green)
 *   busy          — sync in progress (blue)
 *   reader_busy   — reader held by another application; taps are dropped (red)
 *   degraded      — connector OK but realtime WebSocket down (warning state, D6b)
 *
 * D6b: Accepts optional realtimeState param. If connector is ready but RT is
 * disconnected, returns 'degraded' to indicate service is partially available.
 */

import { useState, useEffect } from 'react';
import { nfcConnector } from '@/shared/services/nfcConnectorService';
import type { ConnectionState } from './useRealtimeSocket';

export type NfcIndicatorState = 'disconnected' | 'connecting' | 'no_reader' | 'ready' | 'busy' | 'reader_busy' | 'degraded';

function computeState(realtimeState?: ConnectionState): NfcIndicatorState {
  if (nfcConnector.isBusy) return 'busy';
  const s = nfcConnector.status;
  if (s === 'disconnected') return 'disconnected';
  if (s === 'connecting') return 'connecting';
  if (!nfcConnector.firstNfcDevice) return 'no_reader';

  // Held by another application: the reader is present and the bridge is up,
  // but nothing reaches us. Outranks 'degraded' and 'ready' — realtime being
  // down still lets a tap through, contention does not.
  if (nfcConnector.isReaderContended) return 'reader_busy';

  // D6b: Detect degraded state: connector ready but RT down
  if (realtimeState === 'disconnected') return 'degraded';

  return 'ready';
}

export function useNfcStatus(realtimeState?: ConnectionState): NfcIndicatorState {
  const [state, setState] = useState<NfcIndicatorState>(() => computeState(realtimeState));

  useEffect(() => {
    const update = () => setState(computeState(realtimeState));

    nfcConnector.on('status-changed', update);
    nfcConnector.on('connected', update);
    nfcConnector.on('disconnected', update);
    nfcConnector.on('terminal-ready', update);
    nfcConnector.on('device-discovered', update);
    nfcConnector.on('device-disconnected', update);
    nfcConnector.on('busy-changed', update);
    nfcConnector.on('nfc-reader-busy', update);
    nfcConnector.on('nfc-reader-available', update);

    return () => {
      nfcConnector.off('status-changed', update);
      nfcConnector.off('connected', update);
      nfcConnector.off('disconnected', update);
      nfcConnector.off('terminal-ready', update);
      nfcConnector.off('device-discovered', update);
      nfcConnector.off('device-disconnected', update);
      nfcConnector.off('busy-changed', update);
      nfcConnector.off('nfc-reader-busy', update);
      nfcConnector.off('nfc-reader-available', update);
    };
  }, [realtimeState]);

  return state;
}
